/**
 * reportUtils.js — Pure helper functions for the Reports feature.
 * No React dependency — safe to import anywhere.
 */

export const getUserDatetime = () =>
  new Date().toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });

/**
 * Escape `[N]` citation references to `@@CITE:N@@` tokens so
 * markdown doesn't treat them as link references.
 * Skips content inside fenced code blocks to avoid mangling
 * mermaid diagrams and other code.
 */
export const escapeCitations = (text) => {
  const parts = text.split(/(```[\s\S]*?```)/g);
  return parts
    .map((part, i) =>
      i % 2 === 0 ? part.replace(/\[(\d+)\]/g, "@@CITE:$1@@") : part,
    )
    .join("");
};

/**
 * Close unclosed markdown fences, backticks, bold, and italic.
 * Used during streaming when content is often in a broken state.
 */
export const healMarkdown = (text) => {
  let s = text;
  const fenceCount = (s.match(/```/g) || []).length;
  if (fenceCount % 2 !== 0) s += "\n```";
  const withoutFences = s.replace(/```[\s\S]*?```/g, "");
  if ((withoutFences.match(/`/g) || []).length % 2 !== 0) s += "`";
  const lastLine = s.split("\n").pop() || "";
  if ((lastLine.match(/\*\*/g) || []).length % 2 !== 0) s += "**";
  const singleStars = lastLine.replace(/\*\*/g, "").match(/\*/g) || [];
  if (singleStars.length % 2 !== 0) s += "*";
  return s;
};

/**
 * Strip the `[N] ...` citation footer from markdown text.
 * We render it separately as a styled CitationFooter component.
 */
export const stripCitationFooter = (text) => {
  if (!text) return text;
  const lines = text.split("\n");
  let citationStart = null;
  for (let i = lines.length - 1; i >= 0; i--) {
    const stripped = lines[i].trim();
    if (/^\[\d+\]/.test(stripped)) citationStart = i;
    else if (stripped === "") continue;
    else if (citationStart !== null) break;
  }
  if (citationStart !== null)
    return lines.slice(0, citationStart).join("\n").trimEnd();
  return text;
};

/** Regex for annotation comments: `<!-- @annotation: ... -->` */
export const ANNOTATION_RE = /<!--\s*@annotation:\s*(.*?)\s*-->/g;

/**
 * Extract annotations with their character offsets from content.
 * Used by ReportDocument to render margin notes.
 */
export const extractAnnotations = (text) => {
  const annotations = [];
  let match;
  const re = /<!--\s*@annotation:\s*(.*?)\s*-->/g;
  while ((match = re.exec(text)) !== null) {
    annotations.push({ offset: match.index, text: match[1] });
  }
  return annotations;
};

/** Sentinel reportId for the "new custom report" composer. Nothing exists on
 *  the backend until the user sends their first prompt — see MainReports. */
export const NEW_REPORT_ID = "new";

export const DEFAULT_REPORT_TITLE = "Untitled Report";

/** True when a report still carries the backend's placeholder title. */
export const isUntitledReport = (r) => {
  const title = typeof r?.title === "string" ? r.title.trim() : "";
  return !title || /^untitled(\s+report)?$/i.test(title);
};

/**
 * Zip a report section's optional product arrays into one list.
 *
 * getUniversalReportSection may include `product_images`, `product_title`
 * and `product_risk` (parallel arrays, index-aligned: product N is
 * images[N] / title[N] / risk[N]). They are optional per section, so this
 * returns [] when none are present and callers render nothing extra.
 * Index alignment is preserved — a blank slot stays blank rather than
 * shifting the neighbouring arrays. Rows with nothing in any slot are dropped.
 */
export const getReportProducts = (section) => {
  if (!section || typeof section !== "object") return [];
  const toList = (v) =>
    Array.isArray(v) ? v : v == null || v === "" ? [] : [v];
  const str = (v) => (typeof v === "string" ? v.trim() : "");
  const images = toList(section.product_images);
  const titles = toList(section.product_title);
  const risks = toList(section.product_risk);
  const count = Math.max(images.length, titles.length, risks.length);
  const products = [];
  for (let i = 0; i < count; i++) {
    const image = str(images[i]);
    const title = str(titles[i]);
    const risk = str(risks[i]);
    if (!image && !title && !risk) continue;
    products.push({ image, title, risk });
  }
  return products;
};
